import type { DashboardData } from "@crewledger/shared/schemas";
import {
  AlertTriangle,
  Clock,
  DollarSign,
  HardHat,
  Timer,
  Tractor,
  Users,
} from "lucide-react";
import { StatCard, StatCardSkeleton } from "@/components/design";
import { formatCurrency, formatHours } from "../utils";

interface DashboardKpiGridProps {
  kpis: DashboardData["kpis"] | null;
  currency: string;
  loading: boolean;
}

export function DashboardKpiGrid({ kpis, currency, loading }: DashboardKpiGridProps) {
  if (loading || !kpis) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
        {Array.from({ length: 10 }).map((_, i) => (
          <StatCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  const cards = [
    {
      label: "Hours today",
      value: formatHours(kpis.totalHoursToday),
      icon: Clock,
      accent: "orange" as const,
    },
    {
      label: "Regular hours",
      value: formatHours(kpis.regularHours),
      icon: Timer,
      accent: "navy" as const,
    },
    {
      label: "Overtime",
      value: formatHours(kpis.overtimeHours),
      icon: Timer,
      accent: "amber" as const,
    },
    {
      label: "Double time",
      value: formatHours(kpis.doubleTimeHours),
      icon: Timer,
      accent: "amber" as const,
    },
    {
      label: "Est. gross pay",
      value:
        kpis.estimatedGrossPay != null ? formatCurrency(kpis.estimatedGrossPay, currency) : "—",
      icon: DollarSign,
      accent: "green" as const,
    },
    {
      label: "Active workers",
      value: kpis.activeWorkers,
      icon: Users,
      accent: "navy" as const,
    },
    {
      label: "Equipment in use",
      value: kpis.equipmentInUse,
      icon: Tractor,
      accent: "navy" as const,
    },
    {
      label: "Equipment cost",
      value: kpis.equipmentCost != null ? formatCurrency(kpis.equipmentCost, currency) : "—",
      icon: Tractor,
      accent: "orange" as const,
    },
    {
      label: "Pending approvals",
      value: kpis.pendingApprovals,
      icon: HardHat,
      accent: "amber" as const,
    },
    {
      label: "Exceptions",
      value: kpis.exceptionsNeedingReview,
      icon: AlertTriangle,
      accent: kpis.exceptionsNeedingReview > 0 ? ("amber" as const) : ("green" as const),
    },
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
      {cards.map((card) => (
        <StatCard key={card.label} {...card} />
      ))}
    </div>
  );
}
